import React, { useState, useRef } from 'react';
import tw from 'twin.macro';

import { PlayIconFill } from '@icons/PlayIconFill';

const VideoContainer = tw.div`bg-[#000000] h-full relative `;
const Video = tw.video`h-full`;
const Progressbar = tw.progress`absolute left-0 bottom-0 w-full h-[10px] [&::-webkit-progress-bar]:bg-text-and-icon-muted-2 [&::-webkit-progress-bar]:h-[10px] [&::-webkit-progress-value]:h-[10px] [&::-webkit-progress-value]:bg-error-bg [&::-moz-progress-bar]:bg-error-bg`;
const VideoToggler = tw.button`absolute inset-0 flex items-center justify-center active:outline-none active:border-none focus:outline-none focus:border-none`;
const VideoControls = tw.div`absolute inset-0`;

export const VideoPlayer = ({
  src,
  outerProgressbar = false,
}: {
  src: string;
  outerProgressbar?: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLProgressElement>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [duration, setDuration] = useState<number | undefined>(0);
  const [progress, setProgress] = useState<number>(0);

  const togglePlay = () => {
    if (!videoRef?.current) return;

    if (isPlaying) {
      videoRef?.current.pause();
    } else {
      videoRef?.current.play();
    }

    setIsPlaying(!isPlaying);
  };

  const onTimeUpdate = () => {
    if (!videoRef?.current) return;
    setProgress(videoRef.current.currentTime);
  };

  const onEnded = () => {
    if (!videoRef.current) return;
    setIsPlaying(false);
  };

  const onCanPlay = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current?.duration);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLProgressElement>) => {
    if (!e || !videoRef.current || !duration) return;

    const { target } = e;
    const parent = (target as HTMLProgressElement)?.parentNode?.parentNode as HTMLElement;

    const positionInParent = e.clientX - parent.offsetLeft;
    const parentWidth = parent.getBoundingClientRect().width;
    const percentage = (positionInParent / parentWidth) * 100;

    const newTime = (duration / 100) * percentage;
    videoRef.current.currentTime = newTime;
  };

  return (
    <>
      <VideoContainer>
        <Video
          src={src}
          ref={videoRef}
          onTimeUpdate={onTimeUpdate}
          onCanPlay={onCanPlay}
          onEnded={onEnded}
          width='auto'
          height='100%'
          controls={false}
          css={[!isPlaying && tw`opacity-70`]}
        />
        <VideoControls>
          <VideoToggler onClick={togglePlay}>
            {!isPlaying && <PlayIconFill tw='stroke-white' data-testid='audio-player-play-icon' />}
          </VideoToggler>
        </VideoControls>
      </VideoContainer>
      {outerProgressbar && (
        <Progressbar
          ref={progressRef}
          value={progress}
          onClick={(e) => handleProgressClick(e as React.MouseEvent<HTMLProgressElement>)}
          max={duration}
        />
      )}
    </>
  );
};
